import { FormattedMessage } from 'react-intl';
import { PaloFormGrid } from '@synergy/components-palo';
import { PoolMarkdownEditor } from '@synergy/components';
import { ReactElement, ReactNode } from 'react';
import { useFormikContext } from 'formik';

import { PaloFormikField } from './palo-formik-field';
import { PaloTextControl } from './palo-text-control';

/** Two lines to start with, room for about twelve before the field scrolls. */
const EDITOR_MIN_HEIGHT = 56;
const EDITOR_MAX_HEIGHT = 264;
const INTERNAL_MIN_ROWS = 2;
const INTERNAL_MAX_ROWS = 12;

export interface PaloRemarkPairProps {
    /** The remark that leaves the house, formatted (to the recipient, supplier or member). */
    external: { name: string; label: ReactNode; onChange?: (value: string) => void };
    /**
     * The internal remark, plain text. Left out on documents that have none. It
     * stays writable on a closed document: it is a note for the house, not part
     * of what went out, and the backend accepts it there (#2217).
     */
    internalName?: string;
    /** Locks the remark that went out. */
    readonly?: boolean;
}

/**
 * Both remarks of a document, always open and side by side under the positions.
 * Each starts at about two lines and grows with its text, so one sees at a glance
 * whether there is a remark and can write without another click (GF 14.10.2026,
 * one rule for every document).
 */
export const PaloRemarkPair = ({ external, internalName, readonly }: PaloRemarkPairProps): ReactElement => {
    const formik = useFormikContext();

    return (
        <PaloFormGrid columns={2}>
            <PaloFormikField<string> name={external.name} label={external.label}>
                {({ field, hasError }) => (
                    <PoolMarkdownEditor
                        name={field.name}
                        value={field.value ?? ''}
                        minHeight={EDITOR_MIN_HEIGHT}
                        maxHeight={EDITOR_MAX_HEIGHT}
                        onChange={value =>
                            external.onChange ? external.onChange(value) : formik.setFieldValue(field.name, value)
                        }
                        error={hasError}
                        readOnly={readonly}
                    />
                )}
            </PaloFormikField>
            {internalName && (
                <PaloTextControl
                    name={internalName}
                    label={<FormattedMessage id="app.header.internalNote" />}
                    multiline
                    minRows={INTERNAL_MIN_ROWS}
                    maxRows={INTERNAL_MAX_ROWS}
                    links
                />
            )}
        </PaloFormGrid>
    );
};
